
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  RcsBrand,
  RcsDedicatedFormState,
  RcsSelectedCarrier,
  RcsWizardStepId,
  isCarrierRequirementsComplete,
  isCarrierSelectionComplete,
  isRepresentativeComplete,
  isTestNumbersComplete,
  isVisualDesignComplete,
} from '@/types/rcs-onboarding';
import RcsOnboardingProgress from './RcsOnboardingProgress.vue';
import RcsOnboardingNav from './RcsOnboardingNav.vue';
import BasicInfoStep from './steps/BasicInfoStep.vue';
import CarrierSelectionStep from './steps/CarrierSelectionStep.vue';
import CarrierRequirementsStep from './steps/CarrierRequirementsStep.vue';
import VisualDesignStep from './steps/VisualDesignStep.vue';
import RepresentativeStep from './steps/RepresentativeStep.vue';
import TestNumbersStep from './steps/TestNumbersStep.vue';

const SENDER_ID_REGEX = /^[A-Za-z0-9_-]{2,50}$/;

export default defineComponent({
  name: 'RcsOnboardingWizard',

  components: {
    RcsOnboardingProgress,
    RcsOnboardingNav,
    BasicInfoStep,
    CarrierSelectionStep,
    CarrierRequirementsStep,
    VisualDesignStep,
    RepresentativeStep,
    TestNumbersStep,
  },

  props: {
    currentStep: {
      type: String as PropType<RcsWizardStepId>,
      required: true,
    },
    currentStepIndex: {
      type: Number,
      required: true,
    },
    steps: {
      type: Array as PropType<RcsWizardStepId[]>,
      default: () => [],
    },
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
    reusableBrands: {
      type: Array as PropType<RcsBrand[]>,
      default: () => [],
    },
    availableCarriers: {
      type: Array as PropType<RcsSelectedCarrier[]>,
      default: () => [],
    },
    carriersLoading: {
      type: Boolean,
      default: false,
    },
    carriersError: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    agentIsDirty: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['back', 'next', 'submit', 'save-and-continue', 'reload-carriers'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const stepNumber = computed(() => props.currentStepIndex + 1);
    const isFirstStep = computed(() => props.currentStepIndex === 0);
    const isLastStep = computed(
      () => props.currentStepIndex === props.steps.length - 1,
    );

    // Per-step gating: prevents merchants from jumping forward with missing data.
    const canGoNext = computed(() => {
      switch (props.currentStep) {
        case 'basic_info': {
          const b = props.form.basic_info;
          return (
            !!b.brand_display_name.trim()
            && !!b.agent_display_name.trim()
            && SENDER_ID_REGEX.test(b.sender_id)
          );
        }
        case 'carrier_selection':
          return isCarrierSelectionComplete(props.form.carrier_selection);
        case 'visual':
          return isVisualDesignComplete(props.form.visual);
        case 'representative':
          return isRepresentativeComplete(props.form.representative);
        case 'test_numbers':
          return isTestNumbersComplete(props.form.test_numbers);
        case 'carrier_requirements':
          return isCarrierRequirementsComplete(props.form.carrier_requirements);
        default:
          return true;
      }
    });

    const nextButtonLabel = computed(() => (
      t('storeParameters.services.rcsServiceParameter.nav.saveAndContinue')
    ));

    const handleNext = () => {
      emit('save-and-continue');
    };

    return {
      stepNumber,
      isFirstStep,
      isLastStep,
      canGoNext,
      nextButtonLabel,
      handleNext,
    };
  },
});
